// EP-160 T160-104l (S6, D6, AC-11): ข้อมูลของฉัน, the tenant's PDPA page.
//
// The header is the operator's privacy notice (controller, contact, FlowRent
// as processor, policy link). Below it the tenant can raise any of the six
// data-subject requests and follow them; a completed access request carries a
// download link minted behind the tenant's login.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { ChevronLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useAccount } from '@/hooks/useAccount'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import {
  NOTE_REQUIRED_TYPES,
  fetchDataSubjectExportUrl,
  openDataSubjectExport,
  useCreateDataSubjectRequest,
  useDataSubjectRequests,
  type CreateDataSubjectRequestInput,
  type DataSubjectRequest,
  type DataSubjectRequestStatus,
  type DataSubjectRequestType,
} from '@/hooks/useDataSubjectRequests'

const OTHER_TYPES: DataSubjectRequestType[] = ['rectification', 'restriction', 'objection', 'withdrawal']

const STATUS_BADGE: Record<DataSubjectRequestStatus, string> = {
  pending: 'status-badge-warning',
  processing: 'status-badge-info',
  completed: 'status-badge-success',
  rejected: 'status-badge-error',
}

function errorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined
  return (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code
}

export default function MyDataPage() {
  const { t } = useTranslation('my_data')
  const { t: tc } = useTranslation('common')
  const create = useCreateDataSubjectRequest()
  const [sheet, setSheet] = useState<'erasure' | 'other' | null>(null)
  const [sent, setSent] = useState(false)

  const submit = (input: CreateDataSubjectRequestInput) => {
    setSent(false)
    create.mutate(input, {
      onSuccess: () => {
        setSheet(null)
        setSent(true)
      },
    })
  }

  // A sheet shows its own error; the page only shows one for the access button.
  const pageError = create.isError && sheet === null

  return (
    <div className="space-y-4 pb-4 px-4 pt-4">
      <div className="flex items-center gap-2">
        <Link to="/" aria-label={tc('actions.back')} className="w-8 h-8 -ml-1 flex items-center justify-center text-base-content/70">
          <ChevronLeftIcon className="w-5 h-5" />
        </Link>
        <div className="icon-container">
          <ShieldCheckIcon className="w-5 h-5" />
        </div>
        <h1 className="text-lg font-bold text-base-content">{t('title')}</h1>
      </div>

      <PrivacyHeader />

      <div className="flat-card p-4 space-y-3">
        <RequestButton
          label={t('access_button')}
          hint={t('access_hint')}
          disabled={create.isPending}
          onClick={() => submit({ request_type: 'access' })}
        />
        <RequestButton
          label={t('erasure_button')}
          hint={t('erasure_hint')}
          disabled={create.isPending}
          onClick={() => {
            create.reset()
            setSheet('erasure')
          }}
        />
        <RequestButton
          label={t('other_button')}
          disabled={create.isPending}
          onClick={() => {
            create.reset()
            setSheet('other')
          }}
        />
        {sent && (
          <div role="status" className="alert alert-success text-sm">
            <span>{t('sent')}</span>
          </div>
        )}
        {pageError && (
          <div role="alert" className="alert alert-error text-sm">
            <span>{t('error')}</span>
          </div>
        )}
      </div>

      <RequestList />

      {sheet === 'erasure' && (
        <ErasureSheet
          isPending={create.isPending}
          failed={create.isError}
          onCancel={() => setSheet(null)}
          onConfirm={() => submit({ request_type: 'erasure' })}
        />
      )}
      {sheet === 'other' && (
        <OtherRequestSheet
          isPending={create.isPending}
          serverError={create.isError ? errorCode(create.error) ?? 'error' : null}
          onCancel={() => setSheet(null)}
          onSubmit={submit}
        />
      )}
    </div>
  )
}

// The operator's notice. An older bundled account snapshot carries no privacy
// block, so nothing is shown for it rather than a half-filled notice.
function PrivacyHeader() {
  const { t } = useTranslation('my_data')
  const { account } = useAccount()
  const privacy = account?.privacy
  if (!privacy) return null

  return (
    <div className="flat-card p-4 space-y-1">
      <p className="text-sm font-semibold text-base-content">
        {t('controller', { name: privacy.controller_name })}
      </p>
      <p className="text-xs text-base-content/70 break-words">
        {t('contact', { contact: privacy.controller_contact })}
        {' · '}
        {t('processor', { processor: privacy.processor })}
      </p>
      <a
        href={privacy.policy_url}
        target="_blank"
        rel="noopener noreferrer"
        className="link link-primary text-xs"
      >
        {t('policy_link', { processor: privacy.processor })}
      </a>
    </div>
  )
}

interface RequestButtonProps {
  label: string
  hint?: string
  disabled: boolean
  onClick: () => void
}

function RequestButton({ label, hint, disabled, onClick }: RequestButtonProps) {
  return (
    <div className="space-y-1">
      <button className="btn btn-outline btn-primary btn-sm w-full h-auto min-h-8 py-1.5 whitespace-normal" disabled={disabled} onClick={onClick}>
        {label}
      </button>
      {hint && <p className="text-xs text-base-content/60 text-center">{hint}</p>}
    </div>
  )
}

function RequestList() {
  const { t } = useTranslation('my_data')
  const { data: requests, isLoading, error } = useDataSubjectRequests()

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-base-content">{t('requests_title')}</h2>
      {isLoading && <LoadingSpinner />}
      {error && (
        <div role="alert" className="alert alert-error text-sm">
          <span>{t('error')}</span>
        </div>
      )}
      {requests && requests.length === 0 && (
        <p className="text-xs text-base-content/50 py-2">{t('requests_empty')}</p>
      )}
      {requests && requests.length > 0 && (
        <ul className="space-y-2">
          {requests.map((request) => (
            <RequestRow key={request.id} request={request} />
          ))}
        </ul>
      )}
    </div>
  )
}

function RequestRow({ request }: { request: DataSubjectRequest }) {
  const { t } = useTranslation('my_data')
  const { formatDate } = useDateFormatter()
  const [opening, setOpening] = useState(false)
  // 'expired' also covers a link that lapsed between loading the list and the tap.
  const [exportError, setExportError] = useState<'expired' | 'not_ready' | 'error' | null>(null)

  // A completed access request whose export once existed but is no longer
  // ready has expired (the server only reports export_ready while it is live).
  const expired =
    exportError === 'expired' ||
    (request.request_type === 'access' &&
      request.status === 'completed' &&
      !request.export_ready &&
      !!request.export_expires_at)
  const showLink = request.request_type === 'access' && request.export_ready && !expired

  const openExport = async () => {
    setOpening(true)
    setExportError(null)
    try {
      const url = await fetchDataSubjectExportUrl(request.id)
      await openDataSubjectExport(url)
    } catch (e) {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined
      setExportError(status === 410 ? 'expired' : status === 409 ? 'not_ready' : 'error')
    } finally {
      setOpening(false)
    }
  }

  return (
    <li className="flat-card p-3" data-testid={`dsr-row-${request.id}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs text-base-content/60 flex-shrink-0">{formatDate(request.requested_at)}</span>
        <span className="text-sm font-medium text-base-content flex-1 min-w-0 truncate">
          {t(`types.${request.request_type}`)}
        </span>
        <span className={STATUS_BADGE[request.status]}>{t(`statuses.${request.status}`)}</span>
      </div>
      {showLink && (
        <button
          className="btn btn-link btn-sm px-0 mt-1 h-auto min-h-0 no-underline"
          disabled={opening}
          onClick={() => { void openExport() }}
        >
          {opening ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : t('export_ready')}
        </button>
      )}
      {request.lifted_at && (
        <p className="text-xs text-base-content/60 mt-1">
          {t('restriction_lifted', { date: formatDate(request.lifted_at) })}
        </p>
      )}
      {expired && <p className="text-xs text-base-content/60 mt-1">{t('export_expired')}</p>}
      {exportError === 'not_ready' && <p className="text-xs text-base-content/60 mt-1">{t('export_not_ready')}</p>}
      {exportError === 'error' && <p role="alert" className="text-xs text-error mt-1">{t('error')}</p>}
    </li>
  )
}

interface SheetFrameProps {
  title: string
  onCancel: () => void
  children: React.ReactNode
}

function SheetFrame({ title, onCancel, children }: SheetFrameProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center pointer-events-auto"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={onCancel} />
      <div className="relative z-10 bg-base-100 rounded-t-2xl w-full max-w-[430px] p-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] space-y-4">
        <h2 className="text-base font-bold text-base-content">{title}</h2>
        {children}
      </div>
    </div>
  )
}

interface ErasureSheetProps {
  isPending: boolean
  failed: boolean
  onCancel: () => void
  onConfirm: () => void
}

function ErasureSheet({ isPending, failed, onCancel, onConfirm }: ErasureSheetProps) {
  const { t } = useTranslation('my_data')
  const { t: tc } = useTranslation('common')
  return (
    <SheetFrame title={t('erasure_confirm_title')} onCancel={onCancel}>
      <p className="text-sm text-base-content/80">{t('erasure_confirm_body')}</p>
      {failed && (
        <div role="alert" className="alert alert-error text-sm">
          <span>{t('error')}</span>
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button className="btn btn-ghost flex-1" onClick={onCancel} disabled={isPending}>
          {tc('actions.cancel')}
        </button>
        <button className="btn btn-error flex-1" onClick={onConfirm} disabled={isPending}>
          {isPending ? <span className="loading loading-spinner loading-sm" /> : t('erasure_confirm_submit')}
        </button>
      </div>
    </SheetFrame>
  )
}

interface OtherRequestSheetProps {
  isPending: boolean
  serverError: string | null
  onCancel: () => void
  onSubmit: (input: CreateDataSubjectRequestInput) => void
}

function OtherRequestSheet({ isPending, serverError, onCancel, onSubmit }: OtherRequestSheetProps) {
  const { t } = useTranslation('my_data')
  const { t: tc } = useTranslation('common')
  const [type, setType] = useState<DataSubjectRequestType>('rectification')
  const [note, setNote] = useState('')
  const [noteMissing, setNoteMissing] = useState(false)
  const noteRequired = NOTE_REQUIRED_TYPES.includes(type)

  const submit = () => {
    const trimmed = note.trim()
    if (noteRequired && !trimmed) {
      setNoteMissing(true)
      return
    }
    setNoteMissing(false)
    onSubmit(trimmed ? { request_type: type, note: trimmed } : { request_type: type })
  }

  const showNoteError = noteMissing || serverError === 'note_required'

  return (
    <SheetFrame title={t('other_title')} onCancel={onCancel}>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-base-content mb-1">{t('other_type_label')}</legend>
        {OTHER_TYPES.map((option) => (
          <label key={option} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="dsr-type"
              className="radio radio-primary radio-sm"
              checked={type === option}
              onChange={() => {
                setType(option)
                setNoteMissing(false)
              }}
            />
            {t(`types.${option}`)}
          </label>
        ))}
      </fieldset>
      <label className="block space-y-1">
        <span className="text-sm font-medium text-base-content">
          {t('other_note_label')}
          {!noteRequired && <span className="text-base-content/50 font-normal"> ({t('other_note_optional')})</span>}
        </span>
        <textarea
          className={`textarea textarea-bordered w-full ${showNoteError ? 'textarea-error' : ''}`}
          rows={3}
          maxLength={1000}
          placeholder={t('other_note_placeholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-invalid={showNoteError}
        />
      </label>
      {showNoteError && <p role="alert" className="text-xs text-error">{t('other_note_required')}</p>}
      {serverError && serverError !== 'note_required' && (
        <div role="alert" className="alert alert-error text-sm">
          <span>{t('error')}</span>
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button className="btn btn-ghost flex-1" onClick={onCancel} disabled={isPending}>
          {tc('actions.cancel')}
        </button>
        <button className="btn btn-primary flex-1" onClick={submit} disabled={isPending}>
          {isPending ? <span className="loading loading-spinner loading-sm" /> : t('other_submit')}
        </button>
      </div>
    </SheetFrame>
  )
}
